/* ---------------------------------------------------------------------------
 * ZAHNHELD LANDINGPAGE
 *
 * Four behaviours for the zh-plp-* sections:
 *
 *   1. Card rails: the arrows move by exactly one card plus the gap and switch
 *      themselves off at the ends, so a dead button never sits there looking
 *      alive.
 *   2. Looping hero video: a pause control, because WCAG 2.2.2 only exempts
 *      motion that stops within five seconds and a loop never does.
 *   3. Review count: the filtered one from Reviews.io, where the summary is set
 *      to the same star threshold as the carousel on the page.
 *   4. Deferred third-party scripts: fetched when their container comes near the
 *      viewport instead of on load.
 *
 * Everything is delegated from the document, so a section the theme editor
 * re-renders needs no re-initialisation. Only the initial button state has to be
 * recomputed, which is what shopify:section:load is for.
 * ------------------------------------------------------------------------- */
(function () {
  'use strict';

  var RAIL = '[data-zh-plp-rail]';

  function reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function railOf(button) {
    var scope = button.closest('[data-zh-plp-section]');
    return scope ? scope.querySelector(RAIL) : null;
  }

  /* One card plus the column gap. Read from the DOM rather than from a setting,
     so a card width change in CSS cannot desynchronise the step. */
  function step(rail) {
    var card = rail.querySelector('.zh-plp-card');
    if (!card) return rail.clientWidth;
    var gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  /* The 2px slack absorbs sub-pixel scroll widths, which otherwise leave the
     end button enabled on a rail that cannot move any further. */
  function sync(rail) {
    var scope = rail.closest('[data-zh-plp-section]');
    if (!scope) return;
    var prev = scope.querySelector('[data-zh-plp-rail-prev]');
    var next = scope.querySelector('[data-zh-plp-rail-next]');
    var max = rail.scrollWidth - rail.clientWidth;
    if (prev) prev.disabled = rail.scrollLeft <= 2;
    if (next) next.disabled = rail.scrollLeft >= max - 2;
  }

  function syncAll(root) {
    var rails = (root || document).querySelectorAll(RAIL);
    Array.prototype.forEach.call(rails, sync);
  }

  document.addEventListener('click', function (event) {
    var arrow = event.target.closest('[data-zh-plp-rail-prev], [data-zh-plp-rail-next]');
    if (arrow) {
      var rail = railOf(arrow);
      if (!rail) return;
      var direction = arrow.hasAttribute('data-zh-plp-rail-prev') ? -1 : 1;
      /* behavior:'smooth' does not consult the reduced-motion setting on its
         own, so the query is asked here. */
      rail.scrollBy({ left: direction * step(rail), behavior: reduced() ? 'auto' : 'smooth' });
    }
  });

  /* scroll does not bubble, so it is caught in the capture phase instead. That
     keeps the listener count at one no matter how many rails the page holds. */
  document.addEventListener(
    'scroll',
    function (event) {
      var target = event.target;
      if (target && target.nodeType === 1 && target.matches && target.matches(RAIL)) sync(target);
    },
    { capture: true, passive: true }
  );

  window.addEventListener('resize', function () {
    syncAll();
  });

  /* The band's video slot, its pause control and its reduced-motion behaviour moved
     to assets/zh-media.js, which drives that slot everywhere in the theme now. */

  /* ---------------------------------------------------------------------------
   * Reviews.io: the filtered review count
   *
   * The carousel section can limit its cards to a minimum rating, and Reviews.io
   * answers that request with a smaller results_count. Where the review summary is
   * set to the same threshold it shows that number instead of the full one, so a
   * page does not state two different counts.
   *
   * What Liquid rendered stays put until this succeeds, and stays put if it never
   * does: the fallback is the metafield count, which is a true number either way.
   * ------------------------------------------------------------------------- */

  /* German thousands separator. Same reasoning as in snippets/zh-plp-rating.liquid:
     an unformatted 5015 reads as a part number. */
  function groupThousands(value) {
    return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  function loadFilteredCount(scope) {
    var target = scope.querySelector('[data-zh-plp-count]');
    var store = scope.getAttribute('data-rio-store');
    var sku = scope.getAttribute('data-rio-sku');
    var min = parseInt(scope.getAttribute('data-rio-min'), 10);
    if (!target || !store || !sku || !(min > 1) || typeof window.fetch !== 'function') return;

    /* per_page=1 because only the aggregate is wanted, not the reviews themselves. */
    var url =
      'https://api.reviews.io/timeline/data?type=product_review&per_page=1' +
      '&store=' + encodeURIComponent(store) +
      '&sku=' + encodeURIComponent(sku) +
      '&minRating=' + min;

    window
      .fetch(url)
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (data) {
        var count = data && data.stats && data.stats.results_count;
        if (typeof count === 'number' && count > 0) {
          target.textContent = groupThousands(count);
        }
      })
      .catch(function () {
        /* Leave the server-rendered count alone. */
      });
  }

  function initReviewCounts(root) {
    var scopes = (root || document).querySelectorAll('[data-zh-plp-rio]');
    Array.prototype.forEach.call(scopes, function (scope) {
      if (scope.dataset.zhPlpRioDone) return;
      scope.dataset.zhPlpRioDone = '1';
      loadFilteredCount(scope);
    });
  }

  /* ---------------------------------------------------------------------------
   * Deferred third-party scripts
   *
   * The Reviews.io sentiment bundle is around 460 kB and sits well below the fold.
   * Loading it with the page would put it in front of everything the visitor came
   * for, so the container asks for it when it gets near the viewport.
   *
   * Two details that are not obvious:
   *
   *   The Reviews.io loader scans the document once per execution and marks what it
   *   mounts with data-mounted. So a second container on the page needs nothing, but
   *   a container the theme editor re-rendered needs the script to run again, which
   *   is why an unmounted widget next to an already-present script tag re-injects.
   *
   *   No IntersectionObserver, no waiting: the script is fetched straight away. That
   *   is the old-browser path, and a missing summary would be worse than an early
   *   fetch.
   * ------------------------------------------------------------------------- */

  var LAZY = '[data-zh-plp-lazy-script]';

  function injectScript(src, replaceExisting) {
    var existing = document.querySelector('script[data-zh-plp-src="' + src + '"]');
    if (existing) {
      if (!replaceExisting) return;
      existing.parentNode.removeChild(existing);
    }
    var node = document.createElement('script');
    node.src = src;
    node.async = true;
    node.setAttribute('data-zh-plp-src', src);
    document.head.appendChild(node);
  }

  function armLazyScript(scope) {
    var src = scope.getAttribute('data-zh-plp-lazy-script');
    if (!src) return;

    function run() {
      /* A widget that is already mounted was mounted by an earlier run of the same
         script, and re-running it would be a download for nothing. */
      var mounted = scope.querySelector('[data-mounted="true"]');
      injectScript(src, !mounted);
    }

    if (typeof window.IntersectionObserver !== 'function') {
      run();
      return;
    }

    var observer = new window.IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          run();
        });
      },
      { rootMargin: '600px 0px' }
    );
    observer.observe(scope);
  }

  function initLazyScripts(root) {
    var scopes = (root || document).querySelectorAll(LAZY);
    Array.prototype.forEach.call(scopes, function (scope) {
      if (scope.dataset.zhPlpLazyDone) return;
      scope.dataset.zhPlpLazyDone = '1';
      armLazyScript(scope);
    });
  }

  function init(root) {
    syncAll(root);
    initReviewCounts(root);
    initLazyScripts(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
